import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Sidebar from '../../components/Sidebar';
import { legalAPI } from '../../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  ShieldAlert, AlertTriangle, CheckCircle, Clock, RefreshCw,
  ChevronRight, AlertCircle, Scale, Search, Users, DollarSign,
  TrendingUp, X,
} from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { toast } from 'sonner';

// ─── Lookup tables ─────────────────────────────────────────────────────────────

const STATUS_CFG = {
  open:               { label: 'Open',               color: '#ef4444', bg: 'bg-red-100',     text: 'text-red-700' },
  under_review:       { label: 'Under Review',        color: '#6366f1', bg: 'bg-indigo-100',  text: 'text-indigo-700' },
  awaiting_evidence:  { label: 'Awaiting Evidence',   color: '#f59e0b', bg: 'bg-amber-100',   text: 'text-amber-700' },
  awaiting_client:    { label: 'Awaiting Client',     color: '#8b5cf6', bg: 'bg-violet-100',  text: 'text-violet-700' },
  in_mediation:       { label: 'In Mediation',        color: '#0ea5e9', bg: 'bg-sky-100',     text: 'text-sky-700' },
  resolved:           { label: 'Resolved',            color: '#10b981', bg: 'bg-emerald-100', text: 'text-emerald-700' },
  closed:             { label: 'Closed',              color: '#94a3b8', bg: 'bg-slate-100',   text: 'text-slate-600' },
  escalated_to_court: { label: 'Escalated to Court',  color: '#dc2626', bg: 'bg-red-100',    text: 'text-red-900' },
};

const URGENCY_CFG = {
  critical: { label: 'Critical', color: '#ef4444', bar: '#ef4444', icon: AlertCircle,   textColor: 'text-red-700',    bg: 'bg-red-50' },
  high:     { label: 'High',     color: '#f59e0b', bar: '#f59e0b', icon: AlertTriangle, textColor: 'text-amber-700',  bg: 'bg-amber-50' },
  medium:   { label: 'Medium',   color: '#3b82f6', bar: '#3b82f6', icon: Clock,         textColor: 'text-blue-700',   bg: 'bg-blue-50' },
  low:      { label: 'Low',      color: '#94a3b8', bar: '#94a3b8', icon: CheckCircle,   textColor: 'text-slate-600',  bg: 'bg-slate-50' },
};

const TYPE_LABELS = {
  client_complaint: 'Client Complaint', document_dispute: 'Document Dispute',
  visa_rejection_dispute: 'Visa Rejection Dispute', fraud_concern: 'Fraud Concern',
  legal_threat: 'Legal Threat', regulatory_compliance: 'Regulatory Compliance',
  fee_refund_dispute: 'Fee / Refund Dispute', misrepresentation_claim: 'Misrepresentation Claim',
  privacy_data_concern: 'Privacy / Data Concern', other: 'Other',
};

const ROLE_LABELS = {
  client: 'Client', case_officer: 'Case Officer', sales_consultant: 'Sales Consultant',
  admin: 'Admin', super_admin: 'Super Admin', legal_counsel: 'Legal Counsel', process_manager: 'Process Manager',
};

const CHART_COLORS = ['#6366f1','#ef4444','#f59e0b','#10b981','#0ea5e9','#8b5cf6','#94a3b8','#dc2626'];

// ─── Shared helpers ───────────────────────────────────────────────────────────

function KPICard({ icon: Icon, label, value, sub, color }) {
  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-slate-500 mb-1">{label}</p>
            <p className="text-3xl font-bold text-slate-900">{value}</p>
            {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
          </div>
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${color}`}>
            <Icon className="w-5 h-5 text-white" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function StatusBadge({ status }) {
  const c = STATUS_CFG[status] || STATUS_CFG.open;
  return <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${c.bg} ${c.text}`}>{c.label}</span>;
}

function UrgencyBadge({ urgency }) {
  const c = URGENCY_CFG[urgency] || URGENCY_CFG.low;
  return <span className={`px-2 py-0.5 rounded-full text-xs font-bold bg-opacity-80 ${c.bg} ${c.textColor}`}>{c.label}</span>;
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-lg p-3 text-xs">
      <p className="font-semibold text-slate-700 mb-1">{label}</p>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: p.color }} />
          <span className="text-slate-600">{p.name}:</span>
          <span className="font-bold">{p.value}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function LegalDashboard() {
  const navigate = useNavigate();
  const location = useLocation();

  const activeTab = location.pathname.includes('/analytics')
    ? 'analytics'
    : location.pathname.includes('/escalations')
      ? 'escalations'
      : 'overview';

  const [loading, setLoading]       = useState(true);
  const [data, setData]             = useState(null);
  const [escalations, setEscalations] = useState([]);
  const [escLoading, setEscLoading] = useState(false);
  const [trendDays, setTrendDays]   = useState(30);
  const [statusFilter, setStatusFilter]   = useState('');
  const [urgencyFilter, setUrgencyFilter] = useState('');
  const [search, setSearch]         = useState('');

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const res = await legalAPI.getAnalytics(90);
      setData(res.data);
    } catch { toast.error('Failed to load analytics'); }
    finally { setLoading(false); }
  }, []);

  const fetchEscalations = useCallback(async () => {
    setEscLoading(true);
    try {
      const params = {};
      if (statusFilter)  params.status  = statusFilter;
      if (urgencyFilter) params.urgency = urgencyFilter;
      const res = await legalAPI.listEscalations(params);
      setEscalations(res.data);
    } catch { toast.error('Failed to load escalations'); }
    finally { setEscLoading(false); }
  }, [statusFilter, urgencyFilter]);

  useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);
  useEffect(() => { fetchEscalations(); }, [fetchEscalations]);

  const kpis       = data?.kpis || {};
  const trendData  = (data?.trend || []).slice(-trendDays);
  const statusData = (data?.by_status || []).map(s => ({
    name: STATUS_CFG[s.status]?.label || s.status,
    value: s.count,
    color: STATUS_CFG[s.status]?.color,
  }));
  const typeData = (data?.by_type || []).slice(0, 8).map(t => ({
    name: TYPE_LABELS[t.type] || t.type, count: t.count,
  }));

  const filtered = escalations.filter(e => {
    if (!search.trim()) return true;
    const t = search.toLowerCase();
    return e.client_name?.toLowerCase().includes(t) ||
           e.ucin?.toLowerCase().includes(t) ||
           e.summary?.toLowerCase().includes(t);
  });

  const fmtDate = (iso) =>
    iso ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <div className="main-content-bare overflow-y-auto bg-slate-50">
        <div className="p-6 space-y-6">

          {/* ── Header ── */}
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-slate-900">
                {activeTab === 'analytics' ? 'Legal Analytics' : activeTab === 'escalations' ? 'Escalations' : 'Legal Dashboard'}
              </h1>
              <p className="text-sm text-slate-500">
                {activeTab === 'analytics'
                  ? 'Trend analysis, risk breakdown & escalation metrics'
                  : activeTab === 'escalations'
                    ? 'All escalation cases — search, filter, and review'
                    : 'Escalation overview, risk exposure & pending actions'}
              </p>
            </div>
            {(kpis.overdue_count ?? 0) > 0 && (
              <Badge className="bg-red-100 text-red-700 border border-red-200 gap-1.5 px-3 py-1 text-xs font-semibold">
                <AlertCircle className="w-3.5 h-3.5" /> {kpis.overdue_count} overdue
              </Badge>
            )}
            <Button variant="outline" size="sm" onClick={() => { fetchAnalytics(); fetchEscalations(); }} className="gap-1.5">
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </Button>
          </div>

          {/* ── Loading skeleton ── */}
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Array(8).fill(0).map((_, i) => (
                <Card key={i} className="animate-pulse"><CardContent className="p-5 h-24" /></Card>
              ))}
            </div>
          ) : (
            <>
              {/* ═══════════════════════════ OVERVIEW ══════════════════════════ */}
              {activeTab === 'overview' && (
                <>
                  {/* KPI row */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <KPICard icon={ShieldAlert}   label="Active Escalations"  value={kpis.total_active ?? 0}             sub="open / in progress"        color="bg-red-500" />
                    <KPICard icon={AlertTriangle} label="Critical / High"      value={kpis.critical_high_priority ?? 0}   sub="need immediate attention"  color="bg-amber-500" />
                    <KPICard icon={CheckCircle}   label="Resolved This Month"  value={kpis.resolved_this_month ?? 0}      sub="closed successfully"       color="bg-teal-500" />
                    <KPICard icon={Clock}         label="Avg Resolution"       value={`${kpis.avg_resolution_days ?? 0}d`} sub="days to close"            color="bg-indigo-500" />
                  </div>

                  {/* Urgency risk cards — mirrors SLA aging in Sales */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {['critical','high','medium','low'].map(u => {
                      const cfg = URGENCY_CFG[u];
                      const Icon = cfg.icon;
                      const entry = (data?.by_urgency || []).find(d => d.urgency === u);
                      const count = entry?.count || 0;
                      const total = (data?.by_urgency || []).reduce((s, d) => s + d.count, 0);
                      const pct = total > 0 ? Math.round((count / total) * 100) : 0;
                      return (
                        <Card key={u} className="border-l-4" style={{ borderLeftColor: cfg.bar }}>
                          <CardContent className="p-4">
                            <div className="flex items-center gap-2 mb-2">
                              <Icon className={`w-4 h-4 ${cfg.textColor}`} />
                              <span className={`text-sm font-semibold ${cfg.textColor}`}>{cfg.label}</span>
                            </div>
                            <p className="text-3xl font-bold text-slate-900">{count}</p>
                            <p className="text-xs text-slate-500 mt-1">{pct}% of all escalations</p>
                            <div className="h-1.5 bg-slate-100 rounded-full mt-2 overflow-hidden">
                              <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, background: cfg.bar }} />
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>

                  {/* Overdue alert */}
                  {(data?.overdue || []).length > 0 && (
                    <Card className="border-l-4" style={{ borderLeftColor: '#ef4444' }}>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-base flex items-center gap-2">
                          <AlertCircle className="w-4 h-4 text-red-500" />
                          Overdue — Open More Than 7 Days Without Update
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="pt-0 space-y-2">
                        {data.overdue.map(e => (
                          <div key={e.id}
                            className="flex items-center justify-between p-3 bg-red-50 border border-red-100 rounded-lg cursor-pointer hover:bg-red-100 transition-colors"
                            onClick={() => navigate(`/legal/escalations/${e.id}`)}>
                            <div className="flex items-center gap-3">
                              <UrgencyBadge urgency={e.urgency} />
                              <div>
                                <p className="font-semibold text-sm text-slate-800">{e.client_name}{e.ucin ? ` · ${e.ucin}` : ''}</p>
                                <p className="text-xs text-slate-500 truncate max-w-md">{e.summary}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3 shrink-0">
                              <span className="text-sm font-bold text-red-600">{e.days_open}d open</span>
                              <ChevronRight className="w-4 h-4 text-slate-400" />
                            </div>
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                  )}

                  {/* Recent escalations table */}
                  <Card>
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-base">Recent Escalations</CardTitle>
                        <Button variant="ghost" size="sm" onClick={() => navigate('/legal/escalations')} className="gap-1 text-teal-600 hover:text-teal-700 h-7">
                          View all <ChevronRight className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent className="p-0">
                      {(data?.recent || []).length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                          <Scale className="w-8 h-8 mb-2 text-slate-300" />
                          <p className="text-sm">No escalations yet</p>
                        </div>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="bg-slate-50 border-b border-slate-200">
                                {['Client / UCIN','Type','Urgency','Status','Raised By','Days Open'].map(h => (
                                  <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-slate-500 whitespace-nowrap">{h}</th>
                                ))}
                                <th className="px-4 py-3" />
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                              {data.recent.map(e => (
                                <tr key={e.id} className="hover:bg-slate-50 cursor-pointer transition-colors" onClick={() => navigate(`/legal/escalations/${e.id}`)}>
                                  <td className="px-4 py-3">
                                    <p className="font-semibold text-slate-900">{e.client_name}</p>
                                    {e.ucin && <p className="text-xs text-teal-600 font-mono">{e.ucin}</p>}
                                  </td>
                                  <td className="px-4 py-3 text-xs text-slate-600">{TYPE_LABELS[e.escalation_type] || e.escalation_type}</td>
                                  <td className="px-4 py-3"><UrgencyBadge urgency={e.urgency} /></td>
                                  <td className="px-4 py-3"><StatusBadge status={e.status} /></td>
                                  <td className="px-4 py-3">
                                    <p className="text-xs text-slate-700">{e.raised_by_name}</p>
                                    <p className="text-xs text-slate-400">{ROLE_LABELS[e.raised_by_role] || e.raised_by_role}</p>
                                  </td>
                                  <td className="px-4 py-3">
                                    <span className={`text-sm font-bold ${e.days_open > 7 ? 'text-red-600' : e.days_open > 3 ? 'text-amber-600' : 'text-slate-700'}`}>
                                      {e.days_open}d
                                    </span>
                                  </td>
                                  <td className="px-4 py-3"><ChevronRight className="w-4 h-4 text-slate-300" /></td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </>
              )}

              {/* ═══════════════════════════ ESCALATIONS ═══════════════════════ */}
              {activeTab === 'escalations' && (
                <>
                  {/* KPI row */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <KPICard icon={ShieldAlert}   label="Active"               value={kpis.total_active ?? 0}             sub="open / in progress"       color="bg-red-500" />
                    <KPICard icon={AlertTriangle} label="Critical / High"       value={kpis.critical_high_priority ?? 0}   sub="need immediate attention" color="bg-amber-500" />
                    <KPICard icon={Users}         label="Unassigned"            value={kpis.unassigned ?? 0}               sub="no counsel assigned"      color="bg-violet-500" />
                    <KPICard icon={DollarSign}    label="Liability Exposure"    value={`₹${((kpis.total_liability_inr || 0) / 100000).toFixed(1)}L`} sub="estimated total risk" color="bg-rose-500" />
                  </div>

                  {/* Filters + table */}
                  <Card>
                    <CardHeader className="pb-3">
                      <div className="flex flex-wrap items-center gap-3">
                        <div className="flex-1 min-w-[200px] relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                            placeholder="Search client, UCIN or summary…"
                            className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none bg-white" />
                        </div>
                        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
                          className="px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white text-slate-600 focus:outline-none">
                          <option value="">All Statuses</option>
                          {Object.entries(STATUS_CFG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                        </select>
                        <select value={urgencyFilter} onChange={e => setUrgencyFilter(e.target.value)}
                          className="px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white text-slate-600 focus:outline-none">
                          <option value="">All Urgency</option>
                          {Object.entries(URGENCY_CFG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                        </select>
                        {(statusFilter || urgencyFilter || search) && (
                          <Button variant="ghost" size="sm" className="gap-1 h-9"
                            onClick={() => { setStatusFilter(''); setUrgencyFilter(''); setSearch(''); }}>
                            <X className="w-3.5 h-3.5" /> Clear
                          </Button>
                        )}
                        <span className="text-sm text-slate-500 ml-auto">{filtered.length} record{filtered.length !== 1 ? 's' : ''}</span>
                      </div>
                    </CardHeader>
                    <CardContent className="p-0">
                      {escLoading ? (
                        <div className="flex items-center justify-center py-16">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600" />
                        </div>
                      ) : filtered.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-14 text-slate-400">
                          <Scale className="w-8 h-8 mb-2 text-slate-300" />
                          <p className="text-sm font-medium">No escalations found</p>
                          {(statusFilter || urgencyFilter || search) && <p className="text-xs mt-1">Try adjusting your filters</p>}
                        </div>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="bg-slate-50 border-b border-slate-200">
                                {['Client / UCIN','Type','Urgency','Status','Raised By','Assigned To','Days Open','Date',''].map(h => (
                                  <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-slate-500 whitespace-nowrap">{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                              {filtered.map(e => (
                                <tr key={e.id} className="hover:bg-slate-50 cursor-pointer transition-colors" onClick={() => navigate(`/legal/escalations/${e.id}`)}>
                                  <td className="px-4 py-3">
                                    <p className="font-semibold text-slate-900">{e.client_name}</p>
                                    {e.ucin && <p className="text-xs text-teal-600 font-mono">{e.ucin}</p>}
                                  </td>
                                  <td className="px-4 py-3 text-xs text-slate-600 max-w-[150px]">{TYPE_LABELS[e.escalation_type] || e.escalation_type}</td>
                                  <td className="px-4 py-3"><UrgencyBadge urgency={e.urgency} /></td>
                                  <td className="px-4 py-3"><StatusBadge status={e.status} /></td>
                                  <td className="px-4 py-3">
                                    <p className="text-xs font-medium text-slate-700">{e.raised_by_name}</p>
                                    <p className="text-xs text-slate-400">{ROLE_LABELS[e.raised_by_role] || e.raised_by_role}</p>
                                  </td>
                                  <td className="px-4 py-3">
                                    {e.assigned_to_name
                                      ? <p className="text-xs font-medium text-indigo-700">{e.assigned_to_name}</p>
                                      : <span className="text-xs text-slate-400 italic">Unassigned</span>}
                                  </td>
                                  <td className="px-4 py-3">
                                    <span className={`text-sm font-bold ${e.days_open > 7 ? 'text-red-600' : e.days_open > 3 ? 'text-amber-600' : 'text-slate-700'}`}>
                                      {e.days_open}d
                                    </span>
                                  </td>
                                  <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">{fmtDate(e.created_at)}</td>
                                  <td className="px-4 py-3"><ChevronRight className="w-4 h-4 text-slate-300" /></td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </>
              )}

              {/* ═══════════════════════════ ANALYTICS ═════════════════════════ */}
              {activeTab === 'analytics' && (
                <>
                  {/* KPI row */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <KPICard icon={ShieldAlert}   label="Total All Time"       value={kpis.total_all_time ?? 0}           sub="escalations ever raised"   color="bg-indigo-500" />
                    <KPICard icon={CheckCircle}   label="Resolved This Month"  value={kpis.resolved_this_month ?? 0}      sub="closed successfully"       color="bg-teal-500" />
                    <KPICard icon={Clock}         label="Avg Resolution"       value={`${kpis.avg_resolution_days ?? 0}d`} sub="days to close"            color="bg-emerald-500" />
                    <KPICard icon={DollarSign}    label="Liability Exposure"   value={`₹${((kpis.total_liability_inr || 0) / 100000).toFixed(1)}L`} sub="estimated total risk" color="bg-rose-500" />
                  </div>

                  {/* Charts row 1: Trend + Status */}
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <Card className="lg:col-span-2">
                      <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-base">Escalation Trend</CardTitle>
                          <div className="flex items-center gap-1">
                            {[7, 14, 30, 60].map(d => (
                              <Button key={d} size="sm" variant={trendDays === d ? 'default' : 'ghost'}
                                className={`h-7 px-2.5 text-xs ${trendDays === d ? 'bg-indigo-600 hover:bg-indigo-700' : ''}`}
                                onClick={() => setTrendDays(d)}>
                                {d}d
                              </Button>
                            ))}
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        {trendData.length === 0 ? (
                          <div className="flex items-center justify-center h-[220px] text-slate-400 text-sm">No trend data yet</div>
                        ) : (
                          <ResponsiveContainer width="100%" height={220}>
                            <AreaChart data={trendData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                              <defs>
                                <linearGradient id="gRaised" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="5%"  stopColor="#ef4444" stopOpacity={0.2} />
                                  <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
                                </linearGradient>
                                <linearGradient id="gResolved" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="5%"  stopColor="#10b981" stopOpacity={0.2} />
                                  <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                                </linearGradient>
                              </defs>
                              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                              <XAxis dataKey="date"    tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} />
                              <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} />
                              <Tooltip content={<ChartTooltip />} />
                              <Legend iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                              <Area type="monotone" dataKey="raised"   name="Raised"   stroke="#ef4444" strokeWidth={2} fill="url(#gRaised)"   dot={false} />
                              <Area type="monotone" dataKey="resolved" name="Resolved" stroke="#10b981" strokeWidth={2} fill="url(#gResolved)" dot={false} />
                            </AreaChart>
                          </ResponsiveContainer>
                        )}
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="pb-3"><CardTitle className="text-base">By Status</CardTitle></CardHeader>
                      <CardContent>
                        {statusData.length === 0 ? (
                          <div className="flex items-center justify-center h-[160px] text-slate-400 text-sm">No data</div>
                        ) : (
                          <>
                            <ResponsiveContainer width="100%" height={160}>
                              <PieChart>
                                <Pie data={statusData} dataKey="value" cx="50%" cy="50%"
                                  innerRadius={45} outerRadius={70} paddingAngle={3}
                                  label={({ percent }) => percent > 0.07 ? `${Math.round(percent * 100)}%` : ''}
                                  labelLine={false}>
                                  {statusData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                                </Pie>
                                <Tooltip formatter={(v, n) => [`${v}`, n]} />
                              </PieChart>
                            </ResponsiveContainer>
                            <div className="space-y-1.5 mt-2">
                              {statusData.map((s, i) => (
                                <div key={i} className="flex items-center justify-between text-xs">
                                  <div className="flex items-center gap-1.5">
                                    <span className="w-2 h-2 rounded-full shrink-0" style={{ background: s.color }} />
                                    <span className="text-slate-600 truncate">{s.name}</span>
                                  </div>
                                  <span className="font-bold text-slate-800">{s.value}</span>
                                </div>
                              ))}
                            </div>
                          </>
                        )}
                      </CardContent>
                    </Card>
                  </div>

                  {/* Charts row 2: Type bar + Urgency + Who raises */}
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <Card className="lg:col-span-2">
                      <CardHeader className="pb-3"><CardTitle className="text-base">Escalation Type Breakdown</CardTitle></CardHeader>
                      <CardContent>
                        {typeData.length === 0 ? (
                          <div className="flex items-center justify-center h-[220px] text-slate-400 text-sm">No data yet</div>
                        ) : (
                          <ResponsiveContainer width="100%" height={220}>
                            <BarChart data={typeData} layout="vertical" margin={{ top: 0, right: 20, left: 10, bottom: 0 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                              <XAxis type="number" tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} allowDecimals={false} />
                              <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: '#64748b' }} tickLine={false} axisLine={false} width={148} />
                              <Tooltip content={<ChartTooltip />} />
                              <Bar dataKey="count" name="Escalations" radius={[0, 4, 4, 0]}>
                                {typeData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        )}
                      </CardContent>
                    </Card>

                    <div className="space-y-6">
                      <Card>
                        <CardHeader className="pb-3"><CardTitle className="text-base">Urgency Distribution</CardTitle></CardHeader>
                        <CardContent className="space-y-3">
                          {['critical','high','medium','low'].map(u => {
                            const cfg = URGENCY_CFG[u];
                            const entry = (data?.by_urgency || []).find(d => d.urgency === u);
                            const count = entry?.count || 0;
                            const total = (data?.by_urgency || []).reduce((s, d) => s + d.count, 0);
                            const pct = total > 0 ? Math.round((count / total) * 100) : 0;
                            return (
                              <div key={u}>
                                <div className="flex items-center justify-between text-xs mb-1">
                                  <span className={`font-semibold ${cfg.textColor}`}>{cfg.label}</span>
                                  <span className="font-bold text-slate-800">{count} <span className="text-slate-400 font-normal">({pct}%)</span></span>
                                </div>
                                <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                  <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, background: cfg.bar }} />
                                </div>
                              </div>
                            );
                          })}
                        </CardContent>
                      </Card>

                      <Card>
                        <CardHeader className="pb-3"><CardTitle className="text-base">Raised By</CardTitle></CardHeader>
                        <CardContent className="space-y-2">
                          {(data?.by_role || []).length === 0
                            ? <p className="text-xs text-slate-400 text-center py-2">No data yet</p>
                            : (data.by_role || []).sort((a, b) => b.count - a.count).map((r, i) => (
                                <div key={i} className="flex items-center justify-between text-xs">
                                  <span className="text-slate-600">{ROLE_LABELS[r.role] || r.role}</span>
                                  <span className="font-bold text-slate-800 bg-slate-100 px-2 py-0.5 rounded-full">{r.count}</span>
                                </div>
                              ))
                          }
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
